import type { TechnicalSnapshot, IntradayRange, Candle } from '@invest-copilot/shared';
import { stickySupportResistance } from '@invest-copilot/shared';
import type { PriceLineDef } from '../components/CandleChart';

interface Input {
  /** 現價（即時優先，否則 snapshot.price） */
  price?: number;
  /** 開盤價：來自 chart.meta.sessionOpenPrice */
  sessionOpenPrice?: number;
  prevClose?: number;
  dayHigh?: number;
  dayLow?: number;
  snapshot?: TechnicalSnapshot;
  intradayRange?: IntradayRange;
  /** 是否為日內 timeframe — 日線模式下不畫太多瑣碎線 */
  intraday: boolean;  /** 是否為「單一交易日」（1分/5分，range=1d）。漲跌停 / 今日高低 / 預估高低 / 今日開盤 / 昨收
   *  這些「只對今天有意義」的線只在單日畫；多日盤中（30/60分，range=5d跨多天）不畫（只留 swing 支擐壓力 + 成本）。 */
  singleDay?: boolean;  /** 使用者在這檔股票的平均成本價（以股為位、加權平均）。有值 → 畫「💼 我的成本」線 */
  myCostBasis?: number;
  /** 圖表當前 candles（用來算 swing pivot 歷史支撐壓力；日線＋盤中皆適用） */
  candles?: Candle[];
  /** symbol（判斷台股 + 算漲跌停線） */
  symbol?: string;
}

const COLOR_OPEN = '#fbbf24';         // 黃：今日開盤
const COLOR_PREV = '#9aa0a6';         // 灰：昨收
const COLOR_R = '#ef5350';            // 紅：壓力
const COLOR_S = '#26a69a';            // 綠：支擐
const COLOR_BB = 'rgba(167, 139, 250, 0.7)'; // 紫：布林軌
const COLOR_COST = '#22d3ee';         // 青：我的成本（重要、独立色不與别的混淆）
/** 台股升降單位（簡版，用於 cap 漲跌停顯示）。 */
function roundTwTick(v: number, dir: 'up' | 'down'): number {
  const tick = v < 10 ? 0.01 : v < 50 ? 0.05 : v < 100 ? 0.1 : v < 500 ? 0.5 : v < 1000 ? 1 : 5;
  const n = v / tick;
  return (dir === 'up' ? Math.floor(n) : Math.ceil(n)) * tick;
}
/** 把 K 線上要畫的水平線整理出來。priceLines 過多會視覺混亂，這邊限制 8 條以內。 */
export function computeChartPriceLines(input: Input): PriceLineDef[] {
  const { price, sessionOpenPrice, prevClose, dayHigh, dayLow, snapshot, intradayRange, intraday, singleDay: singleDayInput, myCostBasis, candles, symbol } = input;
  const px = price ?? snapshot?.price;
  // 未傳 singleDay 時退回舊行為（intraday 當單日），保持向後相容。
  const singleDay = singleDayInput ?? intraday;
  const lines: PriceLineDef[] = [];

  // 0. 💼 我的成本（最高優先顯示，独立體線 + 高亮色）
  if (myCostBasis != null && Number.isFinite(myCostBasis)) {
    lines.push({
      key: 'my-cost',
      price: myCostBasis,
      title: `💼 我的成本 ${myCostBasis.toFixed(2)}`,
      color: COLOR_COST,
      lineStyle: 0,
      lineWidth: 2,
      includeInAutoscale: false,
    });
  }

  // 1. 今日開盤（只單日畫；多日盤中 / 日線無「今日開盤」概念）
  if (singleDay && sessionOpenPrice != null && Number.isFinite(sessionOpenPrice)) {
    lines.push({
      key: 'open',
      price: sessionOpenPrice,
      title: `開盤 ${sessionOpenPrice.toFixed(2)}`,
      color: COLOR_OPEN,
      lineStyle: 0, // solid，最重要的線
      lineWidth: 1,
      includeInAutoscale: false,
    });
  }

  // 2. 昨收：日線 always 畫（重要心理關卡）；單日盤中只在接近現價（≤2%）才畫線，
  //    遠則靠 OHLC strip 的漲跌%隱含；多日盤中（30/60分）「昨收」是今日為中心的概念、不畫。
  if (prevClose != null && Number.isFinite(prevClose)) {
    const prevNearEnough = !intraday
      ? true
      : singleDay && (px == null || Math.abs(prevClose - px) / px <= 0.02);
    if (prevNearEnough) {
      lines.push({
        key: 'prev',
        price: prevClose,
        title: `昨收 ${prevClose.toFixed(2)}`,
        color: COLOR_PREV,
        lineStyle: 2,
        lineWidth: 1,
        includeInAutoscale: false,
      });
    }
  }

  // 3. 收集所有候選 levels，分壓力 / 支撐，取最近的各 2 條
  type Lvl = { key: string; price: number; title: string; type: 'R' | 'S'; weight: number };
  const candidates: Lvl[] = [];

  // 3a. Swing pivot 歷史支撐壓力（日線＋盤中都算，吃當前 timeframe 的 candles）。
  //     用「突破確認遲滯」分類角色：價格只是觸及 / 假突破不翻，收盤確實突破超過 ATR 緩衝才翻轉。
  if (px != null && candles && candles.length >= 8) {
    const { resistance, support } = stickySupportResistance(candles, px, 2);
    for (const l of resistance) {
      candidates.push({ key: `piv-r-${Math.round(l.price * 100)}`, price: l.price, title: `壓力 ${l.price.toFixed(2)}${l.touches >= 2 ? ` ×${l.touches}` : ''}`, type: 'R', weight: 4 + l.strength });
    }
    for (const l of support) {
      candidates.push({ key: `piv-s-${Math.round(l.price * 100)}`, price: l.price, title: `支撐 ${l.price.toFixed(2)}${l.touches >= 2 ? ` ×${l.touches}` : ''}`, type: 'S', weight: 4 + l.strength });
    }
  }

  if (singleDay) {
    // 單日：預估區間（ATR 70% 信賴）+ 當日實際高低（都是「今天」的概念，跨多天不畫）
    if (intradayRange) {
      if (intradayRange.estimatedHigh != null) {
        candidates.push({ key: 'est-h', price: intradayRange.estimatedHigh, title: `預估高 ${intradayRange.estimatedHigh.toFixed(2)}`, type: 'R', weight: 2 });
      }
      if (intradayRange.estimatedLow != null) {
        candidates.push({ key: 'est-l', price: intradayRange.estimatedLow, title: `預估低 ${intradayRange.estimatedLow.toFixed(2)}`, type: 'S', weight: 2 });
      }
    }
    if (dayHigh != null && Number.isFinite(dayHigh)) {
      candidates.push({ key: 'day-h', price: dayHigh, title: `今日高 ${dayHigh.toFixed(2)}`, type: 'R', weight: 3 });
    }
    if (dayLow != null && Number.isFinite(dayLow)) {
      candidates.push({ key: 'day-l', price: dayLow, title: `今日低 ${dayLow.toFixed(2)}`, type: 'S', weight: 3 });
    }
  }

  // BB 上下軘不畫在 K 線上：日線 20 期統計跟 intraday 當日範圍邏輯不同，
  // 強制納入會把 K 壓平，不納入又看不到→不如不畫。
  // 相關數字已在下方技術面板顯示。

  // MA20 / MA60 不畫水平線：CandleChart 內已有 MA 線（會動的）

  // 台股漲跌停（單日盤中）＝當日價格物理天花板 / 地板。預估高（livePrice+ATR×0.7）/ swing 壓力等
  // 若算出超過漲停，台股當日物理上到不了 → 剔除（讓下方漲停線當唯一壓力天花板），
  // 否則會出現「壓力 / 預估高 477 > 漲停 469」的矛盾（例：8454.TW 現價 460 距漲停 ≤2%）。
  // 漲跌停本身依當日 prevClose、只在單日有意義 → 多日盤中（30/60分）不算、不畫。
  const isTw = !!symbol && /\.(TW|TWO)$/i.test(symbol);
  const twLimitUp = isTw && singleDay && prevClose != null && Number.isFinite(prevClose) ? roundTwTick(prevClose * 1.1, 'up') : null;
  const twLimitDown = isTw && singleDay && prevClose != null && Number.isFinite(prevClose) ? roundTwTick(prevClose * 0.9, 'down') : null;

  // 4. 同側候選：weight 高者優先（swing pivot 4+ > 當日高低 3 > ATR 預估 2），
  //    貪婪挑選並跳過與已選價位太近（< 現價 0.4%）的，避免線重疊；最後按價位排序輸出。
  if (px != null) {
    const tol = px * 0.004;
    const pick = (type: 'R' | 'S') => {
      const pool = candidates.filter((c) =>
        c.type === type
        && (type === 'R' ? c.price > px : c.price < px)
        // 台股盤中：剔除超過漲停的壓力 / 低於跌停的支擐（當日到不了，由漲跌停線代表天花板/地板）
        && (type === 'R' ? (twLimitUp == null || c.price < twLimitUp) : (twLimitDown == null || c.price > twLimitDown))
      ).sort((a, b) => b.weight - a.weight);
      const out: Lvl[] = [];
      for (const c of pool) {
        if (out.length >= 2) break;
        if (out.some((o) => Math.abs(o.price - c.price) < tol)) continue;
        out.push(c);
      }
      return out.sort((a, b) => a.price - b.price);
    };
    const color = (t: 'R' | 'S') => (t === 'R' ? COLOR_R : COLOR_S);
    for (const lvl of [...pick('R'), ...pick('S')]) {
      // 標籤只留「角色 + 價」（壓力 / 支撐 + 價位）；離現價 % 不再寫進標籤（使用者：壓力支撐標籤不用特別寫百分比）。
      lines.push({ key: lvl.key, price: lvl.price, title: lvl.title, color: color(lvl.type), lineStyle: 2, lineWidth: 1 });
    }
  }

  // 漲跌停（台股盤中）：±10% 平常在盤中圖 Y 軸範圍外、畫了看不到，只在「接近」（距現價 ≤5%）才畫警示線。
  // 但「鎖死」時現價已到漲跌停價，原本 `limitUp > px` 條件會剛好把這條最該標的線濾掉，
  // 導致鎖漲停的個股 K 線上甚麼都沒標、看不懂在看什麼（例：2478.TW 鎖漲停 +9.88%，整條平在頂端卻無標籤）
  // → 鎖死時強制畫線並標「🔒 …鎖死」，讓使用者一眼知道「這條頂/底平線就是漲/跌停價」。
  if (twLimitUp != null && twLimitDown != null && px != null) {
    const limitUp = twLimitUp;
    const limitDown = twLimitDown;
    const lockedUp = px >= limitUp - 1e-6;
    const lockedDown = px <= limitDown + 1e-6;
    if (lockedUp || (limitUp > px && (limitUp - px) / px <= 0.05)) {
      lines.push({ key: 'limit-up', price: limitUp, title: lockedUp ? `🔒 漲停 ${limitUp.toFixed(2)} 鎖死` : `漲停 ${limitUp.toFixed(2)}`, color: COLOR_R, lineStyle: 0, lineWidth: 2, includeInAutoscale: false });
    }
    if (lockedDown || (limitDown < px && (px - limitDown) / px <= 0.05)) {
      lines.push({ key: 'limit-down', price: limitDown, title: lockedDown ? `🔒 跌停 ${limitDown.toFixed(2)} 鎖死` : `跌停 ${limitDown.toFixed(2)}`, color: COLOR_S, lineStyle: 0, lineWidth: 2, includeInAutoscale: false });
    }
  }

  // 去重（同價的線只保留第一個）
  const seen = new Set<number>();
  return lines.filter((l) => {
    const k = Math.round(l.price * 100);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
